import { useCallback, useEffect, useState } from "react";
import { Gavel, ShieldCheck, ShieldAlert, FlaskConical, ScrollText, Play, Check, X } from "lucide-react";
import { Card } from "@/components/common/PageHeader";
import { Loading, Stat } from "@/pages/_shared";
import { toast } from "sonner";
import api from "@/lib/api";

const RISK_CLS = {
  low: "bg-emerald-50 text-emerald-600", medium: "bg-amber-50 text-amber-700",
  high: "bg-orange-50 text-orange-600", critical: "bg-red-50 text-red-600",
};
const CTX_FLAGS = [
  ["customer_consent", "Customer consent on file"],
  ["workflow_active", "Workflow is active"],
  ["agent_has_permission", "Agent has permission"],
  ["product_permits", "Product permits this"],
  ["data_purpose_approved", "Data purpose approved"],
  ["owner_approved", "Owner approved"],
  ["external_recipient", "External recipient (email)"],
];

export default function AIGovernance() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("tools");
  const [simTool, setSimTool] = useState("");
  const [ctx, setCtx] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { api.get("/ai-governance/overview").then(({ data }) => { setData(data); if (!simTool && data.tools[0]) setSimTool(data.tools[0].name); }); }, [simTool]);
  useEffect(() => { load(); }, [load]);
  if (!data) return <Loading />;
  const { tools, rules, decisions, stats } = data;

  const patchTool = async (id, updates) => { try { await api.patch(`/ai-governance/tools/${id}`, updates); load(); } catch { toast.error("Could not update tool."); } };
  const patchRule = async (id, updates) => { try { await api.patch(`/ai-governance/rules/${id}`, updates); load(); } catch { toast.error("Could not update rule."); } };
  const evaluate = async () => {
    setBusy(true); setResult(null);
    try { const { data } = await api.post("/ai-governance/evaluate", { tool: simTool, context: ctx }); setResult(data); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Evaluation failed."); } finally { setBusy(false); }
  };

  const TABS = [["tools", "Tool Registry", ShieldCheck], ["rules", "Policy Rules", Gavel], ["sim", "Policy Simulator", FlaskConical], ["log", "Decision Log", ScrollText]];

  return (
    <div className="animate-fade-up space-y-6" data-testid="ai-governance-page">
      <div>
        <h1 className="flex items-center gap-2 font-head text-2xl font-extrabold tracking-tight text-slate-900"><Gavel size={22} className="text-[#b0862f]" /> AI Governance &amp; Safety</h1>
        <p className="mt-1 text-[13px] text-slate-500">No AI agent gets unrestricted access. Every tool is registered with a risk level and permissions, and the Policy Engine decides ALLOW / DENY — and logs why.</p>
      </div>

      <div className="grid gap-3 sm:grid-cols-4">
        <Stat label="Registered tools" value={stats.tools} tone="slate" />
        <Stat label="High / critical" value={stats.high_risk} tone="red" />
        <Stat label="Active rules" value={stats.rules} tone="blue" />
        <Stat label="Denies today" value={stats.denies_today} tone={stats.denies_today ? "red" : "green"} />
      </div>

      <div className="flex flex-wrap gap-5 border-b border-slate-200">
        {TABS.map(([id, label, Icon]) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`gov-tab-${id}`}
            className={`flex items-center gap-1.5 pb-2.5 text-[14px] font-semibold transition-colors ${tab === id ? "border-b-2 border-[#1a5fd0] text-slate-900" : "text-slate-400"}`}>
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>

      {tab === "tools" && (
        <div className="space-y-2" data-testid="tool-registry">
          {tools.map((t) => (
            <Card key={t.id} className="p-4" data-testid="tool-row">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono text-[13px] font-bold text-slate-900">{t.name}</span>
                    <span className={`rounded-full px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wider ${RISK_CLS[t.risk_level]}`}>{t.risk_level}</span>
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wider text-slate-500">{t.data_classification}</span>
                    {t.approval_required && <span className="rounded-full bg-red-50 px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wider text-red-600">approval</span>}
                    {!t.reversible && <span className="rounded-full bg-amber-50 px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wider text-amber-700">irreversible</span>}
                  </div>
                  <div className="mt-1 text-[13px] text-slate-600">{t.purpose}</div>
                  <div className="mt-1 font-mono text-[10px] uppercase tracking-wider text-slate-400">perms {(t.required_permissions || []).join(", ") || "—"} · {t.cost || "no cost"} · {t.rate_limit || "no limit"}</div>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-2">
                  <button onClick={() => patchTool(t.id, { status: t.status === "enabled" ? "disabled" : "enabled" })} data-testid="toggle-tool"
                    className={`rounded-lg px-3 py-1.5 text-[12px] font-semibold ${t.status === "enabled" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{t.status === "enabled" ? "Enabled" : "Disabled"}</button>
                  <label className="flex items-center gap-1 text-[11px] text-slate-500"><input type="checkbox" checked={!!t.approval_required} onChange={(e) => patchTool(t.id, { approval_required: e.target.checked })} data-testid="toggle-approval" /> require approval</label>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {tab === "rules" && (
        <div className="space-y-2" data-testid="policy-rules">
          {rules.map((r) => (
            <Card key={r.id} className="flex items-center justify-between gap-3 p-4" data-testid="rule-row">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-head text-[14px] font-bold text-slate-900">{r.name}</span>
                  <span className={`rounded-full px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wider ${r.effect === "deny" ? "bg-red-50 text-red-600" : "bg-emerald-50 text-emerald-600"}`}>{r.effect}</span>
                </div>
                <div className="mt-1 font-mono text-[10px] uppercase tracking-wider text-slate-400">tool {r.tool} · priority {r.priority} · {(r.conditions || []).map((c) => `${c.key} ${c.op}`).join(", ") || "always"}</div>
              </div>
              <button onClick={() => patchRule(r.id, { enabled: !r.enabled })} data-testid="toggle-rule"
                className={`rounded-lg px-3 py-1.5 text-[12px] font-semibold ${r.enabled ? "bg-[#1a5fd0]/10 text-[#1a5fd0]" : "bg-slate-100 text-slate-400"}`}>{r.enabled ? "Enabled" : "Disabled"}</button>
            </Card>
          ))}
        </div>
      )}

      {tab === "sim" && (
        <div className="grid gap-4 lg:grid-cols-2" data-testid="policy-simulator">
          <Card className="p-5">
            <div className="mb-3 font-mono text-[10px] font-bold uppercase tracking-wider text-slate-400">Evaluate a request</div>
            <label className="block">
              <span className="mb-1 block font-mono text-[10px] uppercase tracking-wider text-slate-400">Tool</span>
              <select value={simTool} onChange={(e) => { setSimTool(e.target.value); setResult(null); }} data-testid="sim-tool-select"
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-[13px] outline-none focus:border-[#1a5fd0]">
                {tools.map((t) => <option key={t.id} value={t.name}>{t.name} ({t.risk_level})</option>)}
              </select>
            </label>
            <div className="mt-3 space-y-1.5">
              {CTX_FLAGS.map(([key, label]) => (
                <label key={key} className="flex items-center gap-2 text-[13px] text-slate-700">
                  <input type="checkbox" checked={!!ctx[key]} onChange={(e) => setCtx({ ...ctx, [key]: e.target.checked })} data-testid={`ctx-${key}`} /> {label}
                </label>
              ))}
            </div>
            <button onClick={evaluate} disabled={busy} data-testid="evaluate-btn" className="mt-4 inline-flex items-center gap-2 rounded-lg bg-[#080d1a] px-5 py-2.5 font-head text-[14px] font-bold text-white hover:bg-[#1a5fd0] disabled:opacity-60"><Play size={15} /> {busy ? "Evaluating…" : "Ask the Policy Engine"}</button>
          </Card>
          <Card className="p-5" data-testid="sim-result">
            {!result ? <div className="flex h-full items-center justify-center text-[13px] text-slate-400">Set the context and evaluate to see the decision.</div> : (
              <div>
                <div className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 font-head text-lg font-extrabold ${result.decision === "ALLOW" ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-600"}`} data-testid="sim-decision">
                  {result.decision === "ALLOW" ? <Check size={20} /> : <X size={20} />} {result.decision}
                </div>
                <div className="mt-3 font-mono text-[10px] uppercase tracking-wider text-slate-400">Reasons</div>
                <ul className="mt-1 space-y-1.5 text-[13px] text-slate-700">
                  {result.reasons.map((r, i) => <li key={i} className="flex gap-2"><span className="text-slate-300">•</span>{r}</li>)}
                </ul>
              </div>
            )}
          </Card>
        </div>
      )}

      {tab === "log" && (
        <Card className="p-4" data-testid="decision-log">
          {decisions.length === 0 ? <div className="py-6 text-center text-[13px] text-slate-400">No decisions logged yet. Try the Policy Simulator.</div> : (
            <div className="space-y-2">
              {decisions.map((d) => (
                <div key={d.id} className="border-b border-slate-50 py-2 last:border-0">
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-2 text-[13px]"><span className={`rounded px-1.5 py-0.5 font-mono text-[9px] font-bold ${d.decision === "ALLOW" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>{d.decision}</span><span className="font-mono text-slate-700">{d.tool}</span></span>
                    <span className="font-mono text-[10px] text-slate-400">{new Date(d.at).toLocaleString()}</span>
                  </div>
                  <div className="mt-0.5 pl-1 text-[12px] text-slate-500">{(d.reasons || []).join(" ")}</div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
